import { fabric } from "fabric";
import { CanvasHandler } from "../../handlers";
import { FabricEvent, FabricObject } from "../../types/FabricCustomType";
import { IObjectDesingner } from "../../types/HandlerObjectType";
import RoundTableProperties from "./properties/RoundTableProperties";
import { ObjectSuperTypesEnum, ObjectTypesEnum, SeatDistribution } from "../../enums";
import { TextFactory } from "./factories";
import { v4 } from "uuid";
import store from "../../redux/store";
import { RoundTableSeatHelper } from "./helpers/RoundTableSeatHelper";

class RoundTable extends RoundTableProperties implements IObjectDesingner {

  private roundTableSeats: number;
  private roundTableRadius: number;

  constructor(canvasHandler: CanvasHandler) {
    super(canvasHandler);
  }

  public onInit() {
    this.canvasHandler.interactionHandler.drawing(ObjectSuperTypesEnum.RoundTable, "crosshair");
    this.canvasHandler.activeShape = null;
  }

  onRotated(event: FabricEvent) {
    throw new Error("Method not implemented.");
  }

  onMouseDblClick(event: FabricEvent<Event>) {
    return;
  }

  public onMousedown(event: FabricEvent<MouseEvent>) {
    const {
      properties: { roundTableSeats, roundTableRadius },
    } = store.getState();
    this.roundTableSeats = roundTableSeats;
    this.roundTableRadius = roundTableRadius;
    const pointer = this.canvasHandler.canvas.getPointer(event.e, true);
    this.generateRoundTable(pointer.x, pointer.y);
  }

  public onMousemove(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    return;
  }

  public onMouseup(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    return;
  }

  private createTable() {
    this.canvasHandler.isDown = true;

    const tableGroup = new fabric.Group(undefined, {
      left: 0,
      top: 0,
      originX: "center",
      originY: "center",
      hasControls: true,
      hasBorders: true,
      selectable: true,
      type: ObjectTypesEnum.Group,
      subTargetCheck: true,
    }) as FabricObject<fabric.Group>;

    tableGroup.set({
      id: v4(),
      superType: ObjectSuperTypesEnum.Table,
      category: {
        key: 0,
        name: "",
        color: ""
      },
      label: "",
      displayedLabel: "",
      sectionLabel: "",
      value: "",
    });

    const circle = new fabric.Circle({
      left: 0,
      top: 0,
      originX: "center",
      originY: "center",
      radius: this.roundTableRadius,
      angle: 0,
      fill: "#D1D1D1",
      stroke: "#A1A1A1",
      strokeWidth: 1,
      strokeUniform: true,
      transparentCorners: false,
      opacity: 1,
      objectCaching: !this.canvasHandler.editable,
      type: ObjectTypesEnum.Circle,
      hasControls: false,
      hasBorders: false,
      selectable: true,
      lockMovementX: true,
      lockMovementY: true,
      hoverCursor: "pointer",
    }) as FabricObject<fabric.Circle>;

    circle.set({
      id: v4(),
      label: "",
      displayedLabel: "",
      container: this.canvasHandler.container.id,
      editable: false,
      objectOption: this.canvasHandler.objectOption,
    });

    const text = TextFactory.create({left: 0, top: 0, visible: false, fontSize: 16});
    tableGroup.addWithUpdate(circle);
    tableGroup.addWithUpdate(text);
    return tableGroup;
  }

  private createRoundTableGroup(x: number, y: number) {
    const roundTableGroup = new fabric.Group(undefined, {
      left: 0,
      top: 0,
      angle: 0,
      originX: "center",
      originY: "center",
      hasControls: true,
      hasBorders: true,
      selectable: true,
      type: ObjectTypesEnum.Group,
      subTargetCheck: true,
    }) as FabricObject<fabric.Group>;

    roundTableGroup.setControlsVisibility({
      tl: false, //top-left
      mt: false, // middle-top
      tr: false, //top-right
      ml: false, //middle-left
      mr: false, //middle-right
      bl: false, // bottom-left
      mb: false, //middle-bottom
      br: false, //bottom-right
      mtr: true,
    });

    roundTableGroup.set({
      id: v4(),
      superType: ObjectSuperTypesEnum.RoundTable,
      selectionType: 1,
      category: {
        key: 0,
        name: "",
        color: "#000000"
      },
      seatsQuantity: this.roundTableSeats,
      section: {
        label: "",
        displayedLabel: "",
      },
      tableLabeling:{
        visible: false,
        label: "",
        visibleLabel: ""
      },
      seatLabeling:{
        key: SeatDistribution.NONE,
        startsAt: 1,
        list: []
      },
      emptySpaces: 0,
      maxEmptySpaces: 0
    });
    return roundTableGroup;
  }

  private async generateRoundTable(x: number, y: number) {
    const group = this.createRoundTableGroup(0, 0);
    const tableGroup = this.createTable();
    const table = tableGroup.item(0) as any;
    group.add(tableGroup);

    const maxSeats = RoundTableSeatHelper.calculateMaxSeats(7.5, 8, table);
    const maxEmptySpaces = RoundTableSeatHelper.calculateMaxEmptySpaces(group.seatsQuantity, maxSeats);

    await RoundTableSeatHelper.fillTableWithSeats({
      roundTable: group,
      table,
      seatRadius: 7.5,
      seatGap: 8,
    });

    group.set({ left: x, top: y, maxSeats, maxEmptySpaces, angleHelp: 0 });
    group.addWithUpdate();
    group.setCoords();

    this.canvasHandler.canvas.add(group);
    this.canvasHandler.onAdd(group);
    if (!this.canvasHandler.transactionHandler.active) {
      this.canvasHandler.transactionHandler.save("add");
    }
    this.canvasHandler.isDown = false;
    this.canvasHandler.canvas.requestRenderAll();
  }

  
}

export default RoundTable;
